import React, { useState, useEffect, useCallback } from "react";
import { get, post, patch, del } from "../../../helpers/api_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import {
  Card, CardBody, Row, Col, Table, Button, Spinner,
  Modal, ModalHeader, ModalBody, Form, Label, Input,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageHint from '../../../components/Common/PageHint';

const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";

const EMPTY = {
  Dealer_Name: "",
  Dealer_ContactNumber: "",
  Dealer_Country: "",
  Dealer_Lat: "",
  Dealer_Long: "",
};

export default function DealerPage() {
  document.title = "Bayiler | BUHARA - Admin Paneli";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get(`${ODATA_BASE}/Dealer`);
      setRows(res?.value || []);
    } catch (err) {
      console.error("Dealer fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openAdd = () => { setEditId(null); setForm(EMPTY); setModal(true); };
  const openEdit = (row) => {
    setEditId(row.ID);
    setForm({
      Dealer_Name: row.Dealer_Name || "",
      Dealer_ContactNumber: row.Dealer_ContactNumber || "",
      Dealer_Country: row.Dealer_Country || "",
      Dealer_Lat: row.Dealer_Lat ?? "",
      Dealer_Long: row.Dealer_Long ?? "",
    });
    setModal(true);
  };

  const handleField = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        Dealer_Name: form.Dealer_Name,
        Dealer_ContactNumber: form.Dealer_ContactNumber || null,
        Dealer_Country: form.Dealer_Country || null,
        Dealer_Lat: form.Dealer_Lat !== "" ? parseFloat(form.Dealer_Lat) : null,
        Dealer_Long: form.Dealer_Long !== "" ? parseFloat(form.Dealer_Long) : null,
      };
      if (editId) {
        await patch(`${ODATA_BASE}/Dealer(${editId})`, payload);
        toastr.success("Bayi güncellendi.");
      } else {
        await post(`${ODATA_BASE}/Dealer`, payload);
        toastr.success("Bayi eklendi.");
      }
      setModal(false);
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.Dealer_Name}" bayisini silmek istiyor musunuz?`)) return;
    try {
      await del(`${ODATA_BASE}/Dealer(${row.ID})`);
      toastr.success("Bayi silindi.");
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Silme sırasında hata oluştu.");
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem="Bayiler" />
          <PageHint
            what="Bayiler ve satış noktaları bu ekrandan yönetilir."
            where="Bayilerimiz sayfasındaki ülke kartları. Ülke kodu (TR, DE, FR...) bayrağı ve ülke adını belirler."
            links={[{ label: 'Bayilerimiz', path: '/bayilerimiz' }]}
          />
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Bayiler / Satış Noktaları</h5>
                  <Button color="success" onClick={openAdd}>
                    <i className="bx bx-plus me-1" /> Yeni Bayi Ekle
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="warning" /> <span className="ms-2">Yükleniyor...</span>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Bayi Adı</th>
                          <th>İletişim</th>
                          <th>Ülke</th>
                          <th>Enlem</th>
                          <th>Boylam</th>
                          <th className="text-end">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr><td colSpan={7} className="text-center text-muted py-4">Kayıt bulunamadı.</td></tr>
                        ) : rows.map((row, i) => (
                          <tr key={row.ID}>
                            <td>{i + 1}</td>
                            <td>{row.Dealer_Name}</td>
                            <td>{row.Dealer_ContactNumber || "-"}</td>
                            <td>{row.Dealer_Country || "-"}</td>
                            <td>{row.Dealer_Lat ?? "-"}</td>
                            <td>{row.Dealer_Long ?? "-"}</td>
                            <td className="text-end">
                              <Button color="link" className="text-success p-0 me-3" onClick={() => openEdit(row)}>
                                <i className="mdi mdi-pencil font-size-18" />
                              </Button>
                              <Button color="link" className="text-danger p-0" onClick={() => handleDelete(row)}>
                                <i className="mdi mdi-delete font-size-18" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modal} toggle={() => setModal(false)}>
          <ModalHeader toggle={() => setModal(false)} tag="h4">
            {editId ? "Bayi Düzenle" : "Bayi Ekle"}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSave}>
              <div className="mb-3">
                <Label className="form-label">Bayi Adı</Label>
                <Input name="Dealer_Name" value={form.Dealer_Name} onChange={handleField} required />
              </div>
              <div className="mb-3">
                <Label className="form-label">İletişim Numarası</Label>
                <Input name="Dealer_ContactNumber" value={form.Dealer_ContactNumber} onChange={handleField} />
              </div>
              <div className="mb-3">
                <Label className="form-label">Ülke Kodu (örn. tr, de, BE)</Label>
                <Input name="Dealer_Country" value={form.Dealer_Country} onChange={handleField} />
              </div>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label">Enlem (Lat)</Label>
                    <Input name="Dealer_Lat" type="number" step="any" value={form.Dealer_Lat} onChange={handleField} />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label">Boylam (Long)</Label>
                    <Input name="Dealer_Long" type="number" step="any" value={form.Dealer_Long} onChange={handleField} />
                  </div>
                </Col>
              </Row>
              <div className="text-end">
                <Button type="button" color="light" className="me-2" onClick={() => setModal(false)}>İptal</Button>
                <Button type="submit" color="success" disabled={saving}>
                  {saving ? <Spinner size="sm" className="me-1" /> : null}
                  {editId ? "Güncelle" : "Kaydet"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}
